<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useReportBuilderAsync } from 'dashboard/composables/wgpt/useReportBuilderAsync';
import V4Button from 'next/button/Button.vue';
import Spinner from 'next/spinner/Spinner.vue';
import SettingsFieldSection from 'next/Settings/SettingsFieldSection.vue';
import SettingsAccordion from 'next/Settings/SettingsAccordion.vue';
import InputNext from 'next/input/Input.vue';
import TextArea from 'next/textarea/TextArea.vue';
import ReportChat from '../components/ReportChat.vue';
import ReportPreview from '../components/ReportPreview.vue';
import ConfigSummary from '../components/ConfigSummary.vue';
import CustomReportsConfigEditor from '../components/CustomReportsConfigEditor.vue';

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const BUILT_IN_DIMENSION_KEYS = [
  'user',
  'agent',
  'inbox',
  'contact',
  'conversation',
];

const name = ref('');
const description = ref('');
const eventDefinitionName = ref('');
const eventDefinitionId = ref(null);
const editableConfig = ref({});
const chatRef = ref(null);
const showChat = ref(true);

const report = useMapGetter('wgptCustomReports/getActiveReport');
const uiFlags = useMapGetter('wgptCustomReports/getUIFlags');
const chatMessages = useMapGetter('wgptCustomReports/getChatMessages');

const reportId = computed(() => route.params.reportId);
const latestChatConfig = useMapGetter('wgptCustomReports/getLatestChatConfig');
useReportBuilderAsync(reportId);

const configErrors = computed(() => {
  const metricErrors = {};
  const dimensionErrors = {};
  const schemaProps = editableConfig.value?.event_schema?.properties || {};

  (editableConfig.value?.metrics || []).forEach((metric, index) => {
    if (
      metric.aggregation !== 'count' &&
      metric.property &&
      !schemaProps[metric.property]
    ) {
      metricErrors[index] = metric.label || metric.key || `Metric ${index + 1}`;
    }
  });

  (editableConfig.value?.dimensions || []).forEach((dim, index) => {
    const key = dim.property || dim.source;
    if (key && !BUILT_IN_DIMENSION_KEYS.includes(key) && !schemaProps[key]) {
      dimensionErrors[index] = dim.label || key || `Dimension ${index + 1}`;
    }
  });

  return { metrics: metricErrors, dimensions: dimensionErrors };
});

// The API rejects event properties named after the built-in dimensions; the config editor
// flags them on the property itself.
const hasReservedProperties = computed(() =>
  Object.keys(editableConfig.value?.event_schema?.properties || {}).some(key =>
    BUILT_IN_DIMENSION_KEYS.includes(key)
  )
);

const hasConfigErrors = computed(
  () =>
    Object.keys(configErrors.value.metrics).length > 0 ||
    Object.keys(configErrors.value.dimensions).length > 0 ||
    hasReservedProperties.value
);

watch(
  () => editableConfig.value?.dimensions,
  dimensions => {
    const groupBy = editableConfig.value?.default_group_by;
    if (!groupBy) return;
    const validKeys = [
      ...BUILT_IN_DIMENSION_KEYS,
      ...(dimensions || []).map(d => d.property || d.source).filter(Boolean),
    ];
    if (!validKeys.includes(groupBy)) {
      editableConfig.value = { ...editableConfig.value, default_group_by: '' };
    }
  }
);

// Scroll to bottom whenever messages change
watch(
  chatMessages,
  async () => {
    await nextTick();
    chatRef.value?.scrollToBottom();
  },
  { deep: true }
);

onMounted(async () => {
  try {
    await store.dispatch('wgptCustomReports/show', reportId.value);
  } catch (error) {
    useAlert(error?.message || t('WGPT_CUSTOM_REPORTS.VIEWER.ERROR'));
    router.replace(accountScopedRoute('wgpt_reports_settings_list'));
    return;
  }

  name.value = report.value.name || '';
  description.value = report.value.description || '';
  eventDefinitionName.value = report.value.event_definition_name || '';
  eventDefinitionId.value = report.value.event_definition_id;

  let schema = { properties: {} };
  if (eventDefinitionId.value) {
    try {
      const def = await store.dispatch(
        'wgptEventDefinitions/show',
        eventDefinitionId.value
      );
      schema = def.schema || { properties: {} };
    } catch {
      // Use empty schema if definition can't be loaded
    }
  }

  editableConfig.value = {
    event_name: eventDefinitionName.value,
    event_schema: schema,
    metrics: report.value.config?.metrics || [],
    dimensions: report.value.config?.dimensions || [],
    default_visualization:
      report.value.config?.default_visualization || 'bar_chart',
    default_group_by: report.value.config?.default_group_by || '',
    default_time_granularity:
      report.value.config?.default_time_granularity || 'day',
    default_value_mode: report.value.config?.default_value_mode || 'absolute',
    show_summary_cards: report.value.config?.show_summary_cards ?? false,
  };

  // Load persisted chat history for this report
  store.dispatch('wgptCustomReports/getChatHistory', reportId.value);
});

function applyConfig(config) {
  editableConfig.value = {
    event_name: editableConfig.value.event_name,
    event_schema: config.event_schema || editableConfig.value.event_schema,
    metrics: config.metrics || editableConfig.value.metrics,
    dimensions: config.dimensions || editableConfig.value.dimensions,
    default_visualization:
      config.default_visualization ||
      editableConfig.value.default_visualization,
    default_group_by:
      config.default_group_by || editableConfig.value.default_group_by,
    default_time_granularity:
      config.default_time_granularity ||
      editableConfig.value.default_time_granularity,
    default_value_mode:
      config.default_value_mode || editableConfig.value.default_value_mode,
    show_summary_cards:
      config.show_summary_cards ?? editableConfig.value.show_summary_cards,
  };
  if (config.report_name) {
    name.value = config.report_name;
  }
}

watch(latestChatConfig, config => {
  if (config) applyConfig(config);
});

function onChatSend(message) {
  store.dispatch('wgptCustomReports/chat', {
    message,
    currentConfig: editableConfig.value,
    reportId: reportId.value,
  });
}

async function onChatReset() {
  await store.dispatch('wgptCustomReports/resetChat', reportId.value);
}

function goBack() {
  router.push(accountScopedRoute('wgpt_reports_settings_list'));
}

async function updateReport() {
  if (hasConfigErrors.value) {
    useAlert(t('WGPT_CUSTOM_REPORTS.VALIDATION.FIX_CONFIG_ERRORS'));
    return;
  }

  try {
    if (eventDefinitionId.value) {
      await store.dispatch('wgptEventDefinitions/update', {
        id: eventDefinitionId.value,
        schema: editableConfig.value.event_schema,
      });
    }

    await store.dispatch('wgptCustomReports/update', {
      id: reportId.value,
      name: name.value,
      description: description.value,
      config: {
        metrics: editableConfig.value.metrics,
        dimensions: editableConfig.value.dimensions,
        default_visualization: editableConfig.value.default_visualization,
        default_group_by: editableConfig.value.default_group_by,
        default_time_granularity: editableConfig.value.default_time_granularity,
        default_value_mode: editableConfig.value.default_value_mode,
        show_summary_cards: editableConfig.value.show_summary_cards ?? false,
      },
    });
    useAlert(t('WGPT_CUSTOM_REPORTS.FORM.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(error?.message || t('WGPT_CUSTOM_REPORTS.FORM.UPDATE_ERROR'));
  }
}
</script>

<template>
  <section class="flex flex-col h-full w-full overflow-hidden bg-n-surface-1">
    <div class="h-20 min-h-[3.5rem] py-2 bg-n-surface-1 shrink-0">
      <div class="max-w-7xl w-full mx-auto px-6 h-full flex items-center">
        <h1 class="flex items-center mb-0 text-2xl text-n-slate-12">
          <button
            class="flex items-center p-0 font-normal cursor-pointer text-n-slate-11 text-sm ltr:mr-4 rtl:ml-4"
            @click="goBack"
          >
            <i class="i-lucide-chevron-left -ml-1 text-lg" />
            {{ t('GENERAL_SETTINGS.BACK') }}
          </button>
          <span class="text-xl font-medium text-n-slate-12">
            {{ t('WGPT_CUSTOM_REPORTS.HEADER') }}
          </span>
        </h1>
      </div>
    </div>

    <div class="border-b border-solid border-n-weak/60 shrink-0">
      <div class="max-w-7xl w-full mx-auto pt-4 pb-4 px-6">
        <div class="flex items-center justify-between">
          <h2 class="text-2xl text-n-slate-12 font-medium mb-1">
            {{ name || t('WGPT_CUSTOM_REPORTS.FORM.UPDATE') }}
          </h2>
          <div class="flex gap-2 items-center">
            <V4Button
              :icon="
                showChat ? 'i-lucide-panel-right-close' : 'i-lucide-sparkles'
              "
              variant="faded"
              size="sm"
              :label="t('WGPT_CUSTOM_REPORTS.CHAT.TOGGLE')"
              @click="showChat = !showChat"
            />
            <V4Button
              color="blue"
              size="sm"
              :label="t('WGPT_CUSTOM_REPORTS.FORM.UPDATE')"
              :is-loading="uiFlags.isUpdating"
              @click="updateReport"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="uiFlags.isFetchingItem"
      class="flex-1 flex items-center justify-center"
    >
      <Spinner />
    </div>

    <div v-else class="flex-1 overflow-auto py-8">
      <div class="max-w-7xl w-full mx-auto px-6">
        <div class="flex items-start gap-6 lg:gap-10">
          <div class="flex-1 min-w-0">
            <div class="space-y-6 pt-12 pb-6">
              <div>
                <SettingsFieldSection
                  :label="t('WGPT_CUSTOM_REPORTS.FORM.NAME')"
                >
                  <InputNext
                    v-model="name"
                    :placeholder="
                      t('WGPT_CUSTOM_REPORTS.FORM.NAME_PLACEHOLDER')
                    "
                  />
                </SettingsFieldSection>
                <SettingsFieldSection
                  :label="t('WGPT_CUSTOM_REPORTS.FORM.DESCRIPTION')"
                >
                  <TextArea
                    v-model="description"
                    :placeholder="
                      t('WGPT_CUSTOM_REPORTS.FORM.DESCRIPTION_PLACEHOLDER')
                    "
                    custom-text-area-class="!outline !outline-0"
                  />
                </SettingsFieldSection>
              </div>

              <ReportPreview :config="editableConfig" />

              <SettingsAccordion
                :title="t('WGPT_CUSTOM_REPORTS.VIEWER.SUMMARY')"
              >
                <ConfigSummary
                  :config="editableConfig"
                  :errors="configErrors"
                  @update:config="editableConfig = $event"
                />
              </SettingsAccordion>

              <SettingsAccordion
                :title="t('WGPT_CUSTOM_REPORTS.SUMMARY.ADVANCED_SETTINGS')"
                :default-open="false"
              >
                <CustomReportsConfigEditor
                  v-model="editableConfig"
                  :event-name="eventDefinitionName"
                />
              </SettingsAccordion>
            </div>
          </div>

          <div
            v-if="showChat"
            class="w-2/5 shrink-0 sticky top-6 self-start hidden md:flex flex-col overflow-hidden border border-n-weak rounded-2xl min-h-[calc(100vh-12rem)] mb-4"
          >
            <ReportChat
              ref="chatRef"
              show-reset
              :messages="chatMessages"
              :is-loading="uiFlags.isChatting"
              :is-resetting="uiFlags.isResettingChat"
              :placeholder="t('WGPT_CUSTOM_REPORTS.CHAT.EDIT_PLACEHOLDER')"
              @send="onChatSend"
              @reset="onChatReset"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
